import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, ArrowLeftRight, Gift, Package, Sparkles, X } from "lucide-react";
import { ToyCycleLogo } from "./Logo";

const WELCOME_KEY = "toycycle_welcome_seen";

const STEPS = [
  { icon: <ShoppingCart className="w-5 h-5" />, title: "Buy", body: "Pre-loved toys at a fraction of retail.", color: "bg-blue-500" },
  { icon: <Package className="w-5 h-5" />,      title: "Sell", body: "List in 60 seconds. We handle Royal Mail labels.", color: "bg-[#FF6B6B]" },
  { icon: <ArrowLeftRight className="w-5 h-5" />, title: "Swap", body: "Trade toys with other families.", color: "bg-purple-500" },
  { icon: <Gift className="w-5 h-5" />,         title: "Donate", body: "Give a toy a second life — for free.", color: "bg-green-500" },
];

/**
 * One-time welcome modal for first-ever visitors. Shown once per browser
 * (localStorage flag), AFTER the per-session splash has finished. Skipped if
 * the user has prefers-reduced-motion or has already dismissed it.
 */
export const WelcomeModal = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;
    if (localStorage.getItem(WELCOME_KEY) === "1") return undefined;
    // Wait for splash (1.4s) to finish before popping the welcome.
    const t = setTimeout(() => setVisible(true), 1700);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    localStorage.setItem(WELCOME_KEY, "1");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="welcome-modal"
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 px-4"
    >
      <div className="brutalist-card bg-white p-6 sm:p-8 max-w-lg w-full toycycle-splash-pop">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            <ToyCycleLogo size={48} />
            <div>
              <h2 className="font-black text-2xl leading-tight" style={{ fontFamily: "Outfit" }}>
                Welcome to <span className="text-[#FF6B6B]">Toy</span><span className="text-[#4A90E2]">Cycle</span>!
              </h2>
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-[#FFD93D]" />
                Pre-loved toys, lovingly resold.
              </p>
            </div>
          </div>
          <button
            onClick={dismiss}
            className="p-1 hover:bg-gray-100 rounded-full"
            aria-label="Dismiss"
            data-testid="welcome-dismiss"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-700 mb-4">
          Here&rsquo;s how it works in <strong>30 seconds</strong>:
        </p>

        <ol className="grid grid-cols-2 gap-3 mb-6">
          {STEPS.map((step) => (
            <li
              key={step.title}
              className="flex items-start gap-2 p-3 border-2 border-gray-900 rounded-xl bg-[#FDFBF7]"
              data-testid={`welcome-step-${step.title.toLowerCase()}`}
            >
              <span className={`w-8 h-8 ${step.color} text-white rounded-full flex items-center justify-center flex-shrink-0 border-2 border-gray-900`}>
                {step.icon}
              </span>
              <span className="text-xs">
                <strong className="block text-sm">{step.title}</strong>
                <span className="text-gray-600">{step.body}</span>
              </span>
            </li>
          ))}
        </ol>

        <div className="flex flex-col sm:flex-row gap-2">
          <Link
            to="/browse?transaction_type=sell"
            onClick={dismiss}
            className="brutalist-btn-primary flex-1 text-center"
            data-testid="welcome-cta-browse"
          >
            Start Browsing
          </Link>
          <button
            onClick={dismiss}
            className="brutalist-btn-secondary flex-1"
            data-testid="welcome-cta-skip"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
};
